<template>
  <transition name="auth-fade">
    <div v-if="visible" class="auth-overlay" @mousedown.self="dismiss">
      <transition name="auth-rise">
        <div v-if="visible" class="auth-card" role="dialog" aria-modal="true">
          <button class="auth-close" aria-label="Close" @click="dismiss">
            <i class="close icon"></i>
          </button>

          <!-- HEADER -->
          <header class="auth-head">
            <img class="auth-logo" src="../assets/ethos_gold_logo.png" alt="Ethos Empowers" />
            <h2 class="auth-title">
              {{ mode === "login" ? "Welcome back" : mode === "forgot" ? "Reset password" : "Create your account" }}
            </h2>
            <p class="auth-sub">
              {{
                mode === "login"
                  ? "Log in to continue."
                  : mode === "forgot"
                  ? "We will email you a link to set a new password."
                  : "Updates on competitions and events across Architecture, Planning, Civil Engineering and Design."
              }}
            </p>
          </header>

          <!-- LOGIN -->
          <div v-if="mode === 'login'" class="auth-body">
            <button class="auth-google" @click="googleAuth">
              <img src="https://developers.google.com/identity/images/g-logo.png" />
              <span>Continue with Google</span>
            </button>

            <div class="auth-rule"><span>or</span></div>

            <label class="auth-label" for="auth-email">Email</label>
            <input
              id="auth-email"
              v-model="email"
              type="email"
              class="auth-field"
              placeholder="you@example.com"
              @keyup.enter="login"
            />

            <div class="auth-label-row">
              <label class="auth-label" for="auth-pass">Password</label>
              <a class="auth-link" @click="goForgot">Forgot?</a>
            </div>
            <div class="auth-pw">
              <input
                id="auth-pass"
                v-model="password"
                :type="showPw ? 'text' : 'password'"
                class="auth-field"
                placeholder="Your password"
                @keyup.enter="login"
              />
              <i class="eye icon auth-eye" :class="{ slash: showPw }" @click="showPw = !showPw"></i>
            </div>

            <button class="auth-primary" :disabled="busy" @click="login">
              {{ busy ? "Logging in" : "Log in" }}
            </button>

            <p class="auth-foot">
              New here?
              <a class="auth-link" @click="switchMode('signup')">Create an account</a>
            </p>
          </div>

          <!-- FORGOT PASSWORD -->
          <div v-else-if="mode === 'forgot'" class="auth-body">
            <label class="auth-label" for="auth-fmail">Email</label>
            <input
              id="auth-fmail"
              v-model="email"
              type="email"
              class="auth-field"
              placeholder="you@example.com"
              @keyup.enter="forgotPassword"
            />
            <button class="auth-primary" :disabled="busy" @click="forgotPassword">
              {{ busy ? "Sending" : "Send reset link" }}
            </button>
            <p class="auth-foot">
              <a class="auth-link" @click="switchMode('login')">Back to log in</a>
            </p>
          </div>

          <!-- SIGNUP -->
          <div v-else class="auth-body">
            <!-- STEP 1 -->
            <template v-if="step === 1">
              <button class="auth-google" @click="googleAuth">
                <img src="https://developers.google.com/identity/images/g-logo.png" />
                <span>Continue with Google</span>
              </button>

              <div class="auth-rule"><span>or</span></div>

              <label class="auth-label" for="auth-semail">Email</label>
              <input
                id="auth-semail"
                v-model="email"
                type="email"
                class="auth-field"
                placeholder="you@example.com"
                @keyup.enter="nextStep"
              />

              <label class="auth-label" for="auth-spass">Password</label>
              <div class="auth-pw">
                <input
                  id="auth-spass"
                  v-model="password"
                  :type="showPw ? 'text' : 'password'"
                  class="auth-field"
                  placeholder="At least 6 characters"
                  @keyup.enter="nextStep"
                />
                <i class="eye icon auth-eye" :class="{ slash: showPw }" @click="showPw = !showPw"></i>
              </div>

              <button class="auth-primary" @click="nextStep">Continue</button>

              <p class="auth-foot">
                Already have an account?
                <a class="auth-link" @click="switchMode('login')">Log in</a>
              </p>
            </template>

            <!-- STEP 2 -->
            <template v-else>
              <label class="auth-label">I am a</label>
              <div class="auth-roles" role="tablist">
                <button
                  v-for="r in roles"
                  :key="r.value"
                  class="auth-role"
                  :class="{ active: type === r.value }"
                  role="tab"
                  :aria-selected="type === r.value"
                  @click="type = r.value"
                >
                  <i :class="r.icon" class="icon" aria-hidden="true"></i>
                  <span>{{ r.label }}</span>
                </button>
              </div>

              <label class="auth-label" for="auth-name">{{ isOrg ? "Organisation name" : "Full name" }}</label>
              <input
                id="auth-name"
                v-model="name"
                type="text"
                class="auth-field"
                :placeholder="isOrg ? 'Name of the organisation' : 'Your name'"
              />

              <label class="auth-label">Contact number</label>
              <div class="auth-phone">
                <button
                  ref="dialTrigger"
                  type="button"
                  class="auth-dial"
                  :class="{ open: dialOpen }"
                  aria-haspopup="listbox"
                  :aria-expanded="dialOpen ? 'true' : 'false'"
                  aria-label="Country code"
                  @click="toggleDial"
                >
                  <span>{{ flag(dial.iso) }}</span><span>{{ dial.code }}</span>
                </button>
                <input v-model="phone" type="tel" class="auth-field" placeholder="Phone number" />
              </div>

              <div v-if="dialOpen" ref="dialPanel" class="auth-dial-panel">
                <div class="auth-dial-search">
                  <i class="search icon" aria-hidden="true"></i>
                  <input
                    ref="dialSearch"
                    v-model="dialQuery"
                    type="text"
                    placeholder="Search country or code"
                    aria-label="Search country or code"
                    autocomplete="off"
                    @keydown.down.prevent="moveDial(1)"
                    @keydown.up.prevent="moveDial(-1)"
                    @keydown.enter.prevent="pickDial(dialMatches[dialActive])"
                    @keydown.esc.prevent.stop="closeDial(true)"
                  />
                </div>
                <ul ref="dialList" class="auth-dial-list" role="listbox">
                  <li
                    v-for="(c, i) in dialMatches"
                    :key="c.iso"
                    role="option"
                    :aria-selected="c.iso === dialIso ? 'true' : 'false'"
                    :class="{ active: i === dialActive, selected: c.iso === dialIso }"
                    @mousemove="dialActive = i"
                    @click="pickDial(c)"
                  >
                    <span class="auth-dial-flag">{{ flag(c.iso) }}</span>
                    <span class="auth-dial-name">{{ c.name }}</span>
                    <span class="auth-dial-code">{{ c.code }}</span>
                  </li>
                  <li v-if="!dialMatches.length" class="auth-dial-empty">No matches</li>
                </ul>
              </div>

              <!-- ORGANISATION -->
              <template v-if="isOrg">
                <label class="auth-label" for="auth-leader">Organisation leader</label>
                <input
                  id="auth-leader"
                  v-model="orgLeader"
                  type="text"
                  class="auth-field"
                  placeholder="Full name of the leader"
                />

                <label class="auth-label" for="auth-orgloc">Foundation location</label>
                <input
                  id="auth-orgloc"
                  v-model="location"
                  type="text"
                  class="auth-field"
                  placeholder="City where it was founded"
                />

                <label class="auth-label" for="auth-orgyear">Year of foundation</label>
                <input
                  id="auth-orgyear"
                  v-model="yearOfFoundation"
                  type="number"
                  class="auth-field"
                  :max="new Date().getFullYear()"
                  placeholder="e.g. 1998"
                />
              </template>

              <!-- STUDENT -->
              <template v-if="type === 'Student'">
                <label class="auth-label">Level of education</label>
                <div class="auth-select">
                  <select v-model="levelOfEducation">
                    <option value="" disabled>Select level</option>
                    <option v-for="l in educationLevels" :key="l" :value="l">{{ l }}</option>
                  </select>
                </div>
              </template>

              <!-- PROFESSIONAL -->
              <template v-if="type === 'Professional'">
                <label class="auth-label">Employment status</label>
                <div class="auth-select">
                  <select v-model="employmentStatus">
                    <option value="" disabled>Select status</option>
                    <option v-for="e in employmentStatuses" :key="e" :value="e">{{ e }}</option>
                  </select>
                </div>
              </template>

              <!-- FIELD -->
              <template v-if="showField">
                <label class="auth-label">
                  {{ type === "Faculty" ? "Teaching field" : type === "Professional" ? "Field of work" : "Field of study" }}
                </label>
                <div class="auth-select">
                  <select v-model="fieldOfStudy">
                    <option value="" disabled>Select field</option>
                    <option v-for="f in fieldOptions" :key="f" :value="f">{{ f }}</option>
                  </select>
                </div>
              </template>

              <!-- PROFESSIONAL company -->
              <template v-if="type === 'Professional' && ['Employed', 'Self Employed'].includes(employmentStatus)">
                <label class="auth-label">Company</label>
                <input v-model="companyName" type="text" class="auth-field" placeholder="Where you work" />
              </template>

              <!-- INSTITUTION -->
              <template v-if="showInstitute">
                <label class="auth-label">Institution</label>
                <div class="auth-select">
                  <select v-model="institute">
                    <option value="" disabled>Select institution</option>
                    <option value="Not Listed">Not listed</option>
                    <option v-for="(u, i) in instituteOptions" :key="i" :value="u">{{ u }}</option>
                  </select>
                </div>

                <template v-if="institute === 'Not Listed'">
                  <label class="auth-label">Institution name</label>
                  <input
                    v-model="notListedInstitute"
                    type="text"
                    class="auth-field"
                    placeholder="Type your institution or organisation"
                  />
                </template>
              </template>

              <div class="auth-actions">
                <button class="auth-back" @click="step = 1">&larr; Back</button>
                <button class="auth-primary grow" :disabled="busy" @click="createAccount">
                  {{ busy ? "Creating" : "Create account" }}
                </button>
              </div>
            </template>

            <div class="auth-step" aria-hidden="true">
              <span class="auth-step-num">{{ step }}</span>
              <span class="auth-step-track"><i :style="{ transform: `scaleX(${step / 2})` }"></i></span>
              <span class="auth-step-total">2</span>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
import st from "storerestapi";
import universities from "../universities";
import dialCodes, { flag } from "../utils/dialCodes";

export default {
  name: "auth-dialog",
  mixins: [universities],
  data() {
    return {
      visible: false,
      mode: "signup", // signup | login | forgot
      step: 1,
      busy: false,
      showPw: false,
      email: "",
      password: "",
      name: "",
      dialIso: "IN",
      dialOpen: false,
      dialQuery: "",
      dialActive: 0,
      phone: "",
      type: "",
      levelOfEducation: "",
      employmentStatus: "",
      fieldOfStudy: "",
      companyName: "",
      institute: "",
      notListedInstitute: "",
      orgLeader: "",
      location: "",
      yearOfFoundation: "",
      dbUnis: [],
      roles: [
        { value: "Student", label: "Student", icon: "graduation cap" },
        { value: "Professional", label: "Professional", icon: "briefcase" },
        { value: "Faculty", label: "Faculty", icon: "chalkboard teacher" },
        { value: "Organisation", label: "Organisation", icon: "building outline" },
      ],
      educationLevels: [
        "Middle School",
        "High School",
        "Diploma",
        "Undergraduate",
        "Postgraduate",
        "Doctorate",
      ],
      employmentStatuses: ["Employed", "Self Employed", "None"],
    };
  },
  computed: {
    dial() {
      return dialCodes.find((c) => c.iso === this.dialIso) || dialCodes[0];
    },
    // India first, then the rest alphabetically.
    dialOptions() {
      const india = dialCodes.find((c) => c.iso === "IN");
      return [india, ...dialCodes.filter((c) => c !== india)];
    },
    // Name prefix matches first, then anything containing the query, its
    // digits, or the ISO code.
    dialMatches() {
      const q = this.dialQuery.trim().toLowerCase();
      if (!q) return this.dialOptions;
      const digits = q.replace(/[^0-9]/g, "");
      const starts = [];
      const rest = [];
      for (const c of this.dialOptions) {
        const name = c.name.toLowerCase();
        if (name.startsWith(q)) starts.push(c);
        else if (
          name.includes(q) ||
          c.iso.toLowerCase() === q ||
          (digits && c.code.slice(1).startsWith(digits))
        )
          rest.push(c);
      }
      return [...starts, ...rest];
    },
    isOrg() {
      return this.type === "Organisation";
    },
    showField() {
      if (this.type === "Faculty") return true;
      if (this.type === "Professional")
        return ["Employed", "Self Employed"].includes(this.employmentStatus);
      if (this.type === "Student")
        return (
          this.levelOfEducation &&
          !["Middle School", "High School"].includes(this.levelOfEducation)
        );
      return false;
    },
    showInstitute() {
      if (this.type === "Faculty") return true;
      if (this.type === "Student")
        return (
          this.levelOfEducation &&
          !["Middle School", "High School"].includes(this.levelOfEducation)
        );
      return false;
    },
    fieldOptions() {
      if (this.type === "Student" && this.fields[this.levelOfEducation]) {
        return this.fields[this.levelOfEducation];
      }
      return this.fields["Undergraduate"];
    },
    instituteOptions() {
      const key = {
        Architecture: "#a",
        "Interior Design": "#d",
        Planning: "#p",
        "Civil Engineering": "#c",
        Design: "#d",
      };
      let unis;
      if (Object.keys(key).includes(this.fieldOfStudy)) {
        unis = this.dbUnis
          .filter((u) => u.categories && u.categories.includes(key[this.fieldOfStudy]))
          .map((u) => u.name + ", " + u.city);
      } else {
        unis = this.universities;
      }
      return [...unis].sort();
    },
  },
  mounted() {
    this.initFirebase();
    this.maybeShow();
    this.$bus.$on("openAuth", this.open);
  },
  beforeDestroy() {
    this.$bus.$off("openAuth", this.open);
    document.removeEventListener("mousedown", this.onDialOutside);
  },
  watch: {
    dialQuery() {
      this.dialActive = 0;
    },
  },
  methods: {
    flag,
    toggleDial() {
      if (this.dialOpen) return this.closeDial();
      this.dialOpen = true;
      this.dialQuery = "";
      this.dialActive = Math.max(0, this.dialMatches.findIndex((c) => c.iso === this.dialIso));
      document.addEventListener("mousedown", this.onDialOutside);
      this.$nextTick(() => {
        this.$refs.dialSearch.focus({ preventScroll: true });
        this.scrollDialActive();
        this.$refs.dialPanel.scrollIntoView({ block: "nearest", behavior: "smooth" });
      });
    },
    closeDial(refocus = false) {
      this.dialOpen = false;
      document.removeEventListener("mousedown", this.onDialOutside);
      if (refocus && this.$refs.dialTrigger) this.$refs.dialTrigger.focus();
    },
    onDialOutside(e) {
      const inside = [this.$refs.dialPanel, this.$refs.dialTrigger].some((el) => el && el.contains(e.target));
      if (!inside) this.closeDial();
    },
    moveDial(step) {
      const n = this.dialMatches.length;
      if (!n) return;
      this.dialActive = (this.dialActive + step + n) % n;
      this.$nextTick(this.scrollDialActive);
    },
    scrollDialActive() {
      // Scroll only the list, so the dialog body keeps its own scroll.
      const list = this.$refs.dialList;
      const el = list && list.children[this.dialActive];
      if (!el) return;
      if (el.offsetTop < list.scrollTop) list.scrollTop = el.offsetTop - 4;
      else if (el.offsetTop + el.offsetHeight > list.scrollTop + list.clientHeight)
        list.scrollTop = el.offsetTop + el.offsetHeight - list.clientHeight + 4;
    },
    pickDial(c) {
      if (!c) return;
      this.dialIso = c.iso;
      this.closeDial(true);
    },
    open(mode = "signup") {
      this.mode = mode;
      this.step = 1;
      this.visible = true;
      this.loadUniversities();
    },
    maybeShow() {
      if (this.$route.path !== "/") return;
      if (this.$bus.isLoggedIn) return;
      if (sessionStorage.getItem("authDialogDismissed")) return;
      setTimeout(() => {
        if (!this.$bus.isLoggedIn) {
          this.visible = true;
          this.loadUniversities();
        }
      }, 1200);
    },
    dismiss() {
      this.visible = false;
      sessionStorage.setItem("authDialogDismissed", "1");
    },
    switchMode(mode) {
      this.mode = mode;
      this.step = 1;
    },
    goForgot() {
      this.mode = "forgot";
    },
    initFirebase() {
      try {
        const firebaseConfig = {
          apiKey: "AIzaSyCz4ypQyjtGsDhcp9g1gLnZjfZ-wgmYOug",
          authDomain: "ethosempowers-2281d.firebaseapp.com",
          projectId: "ethosempowers-2281d",
          storageBucket: "ethosempowers-2281d.appspot.com",
          messagingSenderId: "905484472591",
          appId: "1:905484472591:web:68aa7104b6a69c93d36e7a",
        };
        /* eslint-disable-next-line no-undef */
        if (!firebase.apps.length) firebase.initializeApp(firebaseConfig);
      } catch (e) {
        console.log(e);
      }
    },
    loadUniversities() {
      if (this.dbUnis.length) return;
      this.$req
        .get("universities?_limit=-1")
        .then((r) => (this.dbUnis = r.data))
        .catch((e) => console.log(e));
    },
    nextStep() {
      if (!this.$bus.validateEmail(this.email)) {
        this.$bus.notify("Enter a valid email", "error");
        return;
      }
      if (!this.password || this.password.length < 6) {
        this.$bus.notify("Password must be at least 6 characters", "error");
        return;
      }
      this.loadUniversities();
      this.step = 2;
    },
    handleAuthSuccess(data, isNew = false) {
      st.setVar("user", data.user);
      st.setVar("token", data.jwt);
      this.$bus.user = data.user;
      this.$bus.token = data.jwt;
      this.visible = false;
      this.$bus.notify(isNew ? "Account created" : "Logged in");
      if (this.$route.path === "/auth") {
        this.$router.push("/events");
      } else {
        // Pages load the user's own data (event registration, team, files)
        // once on mount. Reload so they pick up the new login; otherwise a
        // registered student on an event page sees no Submissions section.
        window.location.reload();
      }
    },
    getErrorMessage(error) {
      try {
        return error.response.data.data[0].messages[0].message;
      } catch {
        return error.message || "Something went wrong";
      }
    },
    googleAuth() {
      /* eslint-disable-next-line no-undef */
      const provider = new firebase.auth.GoogleAuthProvider();
      /* eslint-disable-next-line no-undef */
      firebase
        .auth()
        .signInWithPopup(provider)
        .then((result) => {
          this.$req
            .post("socialauth", { uid: result.user.uid })
            .then((r) => this.handleAuthSuccess(r.data))
            .catch((e) => {
              this.$bus.notify("Authentication failed", "error");
              console.log(e);
            });
        })
        .catch((e) => console.log(e.message));
    },
    login() {
      this.busy = true;
      this.$baseReq
        .post("auth/local", { identifier: this.email, password: this.password })
        .then((r) => {
          const user = r.data.user;
          delete user["quiz_entries"];
          delete user["qod_entries"];
          this.handleAuthSuccess({ user, jwt: r.data.jwt });
        })
        .catch((e) => this.$bus.notify(this.getErrorMessage(e), "error"))
        .finally(() => (this.busy = false));
    },
    forgotPassword() {
      this.busy = true;
      this.$baseReq
        .post("auth/forgot-password", { email: this.email })
        .then(() => {
          this.$bus.notify("Reset link sent. Check your email.");
          this.mode = "login";
        })
        .catch((e) => this.$bus.notify(this.getErrorMessage(e), "error"))
        .finally(() => (this.busy = false));
    },
    createAccount() {
      if (!this.type) return this.$bus.notify("Select what describes you", "error");
      if (!this.name)
        return this.$bus.notify(this.isOrg ? "Enter the organisation name" : "Enter your name", "error");
      if (!this.phone) return this.$bus.notify("Enter your contact number", "error");
      if (this.isOrg) {
        if (!this.orgLeader) return this.$bus.notify("Enter the organisation leader", "error");
        if (!this.location) return this.$bus.notify("Enter the foundation location", "error");
        const year = parseInt(this.yearOfFoundation, 10);
        if (!year || year < 1800 || year > new Date().getFullYear())
          return this.$bus.notify("Enter a valid year of foundation", "error");
      }

      const acc = {
        username: this.email,
        name: this.name,
        email: this.email,
        password: this.password,
        contact: this.dial.code + " " + this.phone,
        country: this.dial.name,
        type: this.type,
        levelOfEducation: this.levelOfEducation,
        institute: this.institute,
        notListedInstitute: this.notListedInstitute,
        field: this.fieldOfStudy,
        companyName: this.companyName,
        employmentStatus: this.employmentStatus,
      };

      if (this.isOrg) {
        acc.orgLeader = this.orgLeader;
        acc.location = this.location;
        acc.yearOfFoundation = parseInt(this.yearOfFoundation, 10);
      }

      if (["Architecture", "Interior Design"].includes(acc.field)) {
        const uni = this.dbUnis.find((u) => u.name + ", " + u.city === acc.institute);
        if (uni) acc.university = uni.id;
      }

      this.busy = true;
      this.$baseReq
        .post("auth/local/register", acc)
        .then(() => {
          this.$bus.notify("Account created. Please log in.");
          this.switchMode("login");
          this.password = "";
        })
        .catch((e) => this.$bus.notify(this.getErrorMessage(e), "error"))
        .finally(() => (this.busy = false));
    },
  },
};
</script>

<style lang="scss" scoped>
$gold: #b98744;
$gold-deep: #8a5e1e;
$ink: #2b2620;
$muted: #8c8579;
$line: #e7e1d6;
$paper: #fffdfa;

.auth-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(34, 27, 18, 0.5);
  backdrop-filter: blur(7px);
  -webkit-backdrop-filter: blur(7px);
  overflow-y: auto;
}

.auth-card {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 500px;
  max-height: calc(100vh - 48px);
  max-height: calc(100dvh - 48px);
  background: $paper;
  border-radius: 14px;
  padding: 44px 56px 26px;
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02), 0 24px 64px -24px rgba(58, 42, 18, 0.4);
}

.auth-close {
  position: absolute;
  top: 18px;
  right: 18px;
  width: 30px;
  height: 30px;
  padding: 0;
  border-radius: 50%;
  background: transparent;
  color: $muted;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s, background 0.15s;
  i { margin: 0; font-size: 14px; }
  &:hover { color: $ink; background: rgba(140, 133, 121, 0.12); }
}

/* HEADER */
.auth-head {
  flex-shrink: 0;
  text-align: center;
  margin-bottom: 22px;
}
.auth-logo {
  height: 58px;
  object-fit: contain;
  margin-bottom: 18px;
  opacity: 0.95;
}
.auth-title {
  font-family: "Open Sans", Helvetica, sans-serif;
  font-weight: 600;
  font-size: 23px;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: $ink;
  margin: 0 0 8px;
}
.auth-sub {
  font-size: 14px;
  line-height: 1.65;
  color: $muted;
  margin: 0 auto;
  max-width: 65ch;
}

.auth-body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* let the scroll edge breathe past the card padding */
  margin: 0 -10px;
  padding: 2px 10px;
  /* slim, on-brand scrollbar */
  scrollbar-width: thin;
  scrollbar-color: rgba(138, 94, 30, 0.35) transparent;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(138, 94, 30, 0.28);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover { background: rgba(138, 94, 30, 0.45); }
}

/* GOOGLE */
.auth-google {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 12px;
  background: #fff;
  border: 1px solid $line;
  border-radius: 9px;
  font-size: 14px;
  font-weight: 600;
  color: $ink;
  transition: border-color 0.15s, background 0.15s;
  img { height: 18px; }
  &:hover { border-color: darken($line, 12%); background: #fff; }
}

/* DIVIDER */
.auth-rule {
  display: flex;
  align-items: center;
  margin: 20px 0 4px;
  color: $muted;
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  &::before, &::after { content: ""; flex: 1; height: 1px; background: $line; }
  span { padding: 0 14px; }
}

/* LABELS */
.auth-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $muted;
  margin: 20px 0 7px;
}
.auth-label-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  .auth-label { margin-bottom: 7px; }
}

/* UNDERLINE FIELDS */
.auth-field {
  width: 100%;
  padding: 11px 13px;
  border: 1px solid $line;
  border-radius: 9px;
  background: #fff;
  font-size: 15px;
  line-height: 1.4;
  height: auto;
  font-family: inherit;
  color: $ink;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.18s, box-shadow 0.18s;
  &::placeholder { color: lighten($muted, 12%); }
  &:focus {
    border-color: $gold;
    box-shadow: 0 0 0 3px rgba(185, 135, 68, 0.15);
  }
}

/* SELECT */
.auth-dial {
  display: flex;
  align-items: center;
  gap: 7px;
  width: 112px;
  flex: 0 0 auto;
  padding: 0 30px 0 13px;
  position: relative;
  border: 1px solid $line;
  border-radius: 9px;
  background: #fff;
  font-size: 15px;
  font-family: inherit;
  color: $ink;
  white-space: nowrap;
  cursor: pointer;
  outline: none;
  transition: border-color 0.18s, box-shadow 0.18s;
  &::after {
    content: "";
    position: absolute;
    right: 14px;
    top: 50%;
    width: 7px;
    height: 7px;
    border-right: 1.5px solid $muted;
    border-bottom: 1.5px solid $muted;
    transform: translateY(-65%) rotate(45deg);
    transition: transform 0.15s;
  }
  &.open::after { transform: translateY(-25%) rotate(-135deg); }
  &:focus-visible,
  &.open {
    border-color: $gold;
    box-shadow: 0 0 0 3px rgba(185, 135, 68, 0.15);
  }
}
.auth-dial-panel {
  flex-shrink: 0;
  margin-top: 8px;
  border: 1px solid $line;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
}
.auth-dial-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border-bottom: 1px solid $line;
  i.icon { margin: 0; color: $muted; font-size: 13px; }
  input {
    flex: 1;
    min-width: 0;
    padding: 11px 0;
    border: 0;
    outline: none;
    font-size: 14px;
    font-family: inherit;
    color: $ink;
    background: transparent;
    &::placeholder { color: lighten($muted, 12%); }
  }
}
.auth-dial-list {
  position: relative;
  list-style: none;
  margin: 0;
  padding: 4px;
  max-height: 220px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(138, 94, 30, 0.35) transparent;
  li {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 7px;
    font-size: 14px;
    color: $ink;
    cursor: pointer;
    &.active { background: #fdf8ef; }
    &.selected { color: $gold-deep; font-weight: 600; }
  }
  .auth-dial-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .auth-dial-code { color: $muted; font-variant-numeric: tabular-nums; }
  .auth-dial-empty { color: $muted; cursor: default; }
}
.auth-select {
  position: relative;
  &::after {
    content: "";
    position: absolute;
    right: 14px;
    top: 50%;
    width: 7px;
    height: 7px;
    border-right: 1.5px solid $muted;
    border-bottom: 1.5px solid $muted;
    transform: translateY(-65%) rotate(45deg);
    pointer-events: none;
  }
  select {
    width: 100%;
    padding: 11px 30px 11px 13px;
    border: 1px solid $line;
    border-radius: 9px;
    background: #fff;
    font-size: 15px;
    font-family: inherit;
    color: $ink;
    outline: none;
    appearance: none;
    cursor: pointer;
    transition: border-color 0.18s, box-shadow 0.18s;
    &:focus {
      border-color: $gold;
      box-shadow: 0 0 0 3px rgba(185, 135, 68, 0.15);
    }
  }
}

/* PASSWORD */
.auth-pw { position: relative; }
.auth-eye {
  position: absolute;
  right: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: $muted;
  cursor: pointer;
  margin: 0;
  font-size: 14px;
  &:hover { color: $ink; }
}

/* PHONE */
.auth-phone {
  display: flex;
  gap: 14px;
  .auth-field { flex: 1; }
}

/* ROLE CHIPS */
.auth-roles {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 4px;
}
.auth-role {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  text-align: left;
  background: #fff;
  border: 1px solid $line;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: $muted;
  transition: color 0.15s, border-color 0.15s, background 0.15s, box-shadow 0.15s;
  i.icon {
    margin: 0;
    width: 18px;
    font-size: 16px;
    line-height: 1;
    color: lighten($muted, 8%);
    transition: color 0.15s;
  }
  &:hover { color: $ink; border-color: darken($line, 10%); }
  &:hover i.icon { color: $muted; }
  &.active i.icon { color: $gold; }
  &.active {
    color: $gold-deep;
    border-color: $gold;
    background: #fdf8ef;
    box-shadow: 0 0 0 3px rgba(185, 135, 68, 0.12);
  }
}

/* PRIMARY BUTTON */
.auth-primary {
  width: 100%;
  margin-top: 26px;
  padding: 13px;
  border-radius: 9px;
  background: $gold-deep;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.02em;
  transition: background 0.15s, transform 0.1s;
  &:hover { background: darken($gold-deep, 5%); }
  &:active { transform: translateY(1px); }
  &:disabled { opacity: 0.55; cursor: default; }
}

/* ACTIONS */
.auth-actions {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-top: 28px;
  .grow { margin-top: 0; flex: 1; }
}
.auth-back {
  padding: 0;
  background: transparent;
  color: $muted;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  transition: color 0.15s;
  &:hover { color: $ink; }
}

/* LINKS + FOOT */
.auth-link {
  color: $gold-deep;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  &:hover { text-decoration: underline; }
}
.auth-foot {
  text-align: center;
  font-size: 13px;
  color: $muted;
  margin: 22px 0 0;
  .auth-link { font-size: 13px; }
}

/* STEP INDICATOR */
.auth-step {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 24px;
  font-family: "Open Sans", Helvetica, sans-serif;
  font-size: 12px;
  color: $muted;
}
.auth-step-num { color: $gold-deep; font-weight: 600; }
.auth-step-total { color: lighten($muted, 8%); }
.auth-step-track {
  flex: 1;
  height: 1px;
  background: $line;
  overflow: hidden;
  i {
    display: block;
    height: 100%;
    background: $gold;
    transform-origin: left;
    transition: transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
  }
}

/* TRANSITIONS */
.auth-fade-enter-active, .auth-fade-leave-active { transition: opacity 0.25s ease; }
.auth-fade-enter, .auth-fade-leave-to { opacity: 0; }
.auth-rise-enter-active { transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease; }
.auth-rise-leave-active { transition: transform 0.2s ease, opacity 0.2s ease; }
.auth-rise-enter, .auth-rise-leave-to { transform: translateY(12px); opacity: 0; }

@media (max-width: 500px) {
  .auth-card { padding: 40px 24px 24px; }
  .auth-title { font-size: 21px; }
  .auth-role { gap: 8px; padding: 11px 10px; font-size: 13px; }
}
</style>
